import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import ApiBaseUrl from "../constants/apiUrl";
import {
  Users,
  Briefcase,
  Search,
  Download,
  Eye,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar,
  Filter,
  FileText,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
} from "lucide-react";

const CareerApplicationsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [applications, setApplications] = useState([]);
  const [jobsList, setJobsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    under_review: 0,
    shortlisted: 0,
    interview_scheduled: 0,
    hired: 0,
    rejected: 0,
  });

  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    status: searchParams.get("status") || "All",
    jobId: searchParams.get("jobId") || "All",
    city: "All",
    page: parseInt(searchParams.get("page") || "1", 10),
    limit: 20,
  });

  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Quick Status Modal
  const [statusModal, setStatusModal] = useState({
    isOpen: false,
    application: null,
    status: "pending",
    remarks: "",
    sendEmailAlert: true,
    sendSmsAlert: true,
    interviewDate: "",
    interviewTime: "",
    interviewMode: "Online (Google Meet)",
    interviewLink: "",
    saving: false,
  });

  useEffect(() => {
    fetchJobsDropdown();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [filters.page, filters.status, filters.jobId, filters.city]);

  const fetchJobsDropdown = async () => {
    try {
      const authData = JSON.parse(localStorage.getItem("adminAuth"));
      const res = await fetch(`${ApiBaseUrl}/admin/career/jobs?limit=100`, {
        headers: { Authorization: `Bearer ${authData?.token || ""}` },
      });
      const data = await res.json();
      if (data.success) {
        setJobsList(data.data.jobs || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchStats = async () => {
    try {
      const authData = JSON.parse(localStorage.getItem("adminAuth"));
      const res = await fetch(`${ApiBaseUrl}/admin/career/applications/stats`, {
        headers: { Authorization: `Bearer ${authData?.token || ""}` },
      });
      const data = await res.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchApplications = async () => {
    setLoading(true);
    setError(null);
    try {
      const authData = JSON.parse(localStorage.getItem("adminAuth"));
      const query = new URLSearchParams({
        page: filters.page,
        limit: filters.limit,
        ...(filters.search && { search: filters.search }),
        ...(filters.status !== "All" && { status: filters.status }),
        ...(filters.jobId !== "All" && { jobId: filters.jobId }),
        ...(filters.city !== "All" && { city: filters.city }),
      });

      const res = await fetch(`${ApiBaseUrl}/admin/career/applications?${query}`, {
        headers: {
          Authorization: `Bearer ${authData?.token || ""}`,
        },
      });

      const data = await res.json();
      if (data.success) {
        setApplications(data.data.applications || []);
        setTotalPages(data.data.totalPages || 1);
        setTotalRecords(data.data.total || 0);
      } else {
        setError(data.message || "Failed to load applications");
      }
    } catch (err) {
      console.error(err);
      setError("Server error. Failed to load applications list.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, page: 1 }));
    fetchApplications();
  };

  const handleDownloadCV = (appId) => {
    const downloadUrl = `${ApiBaseUrl}/career/application/${appId}/cv`;
    window.open(downloadUrl, "_blank");
  };

  const handleDeleteApplication = async (appId, name) => {
    if (
      !window.confirm(
        `Are you sure you want to permanently delete the application of "${name}"? This will also remove their CV file from the server storage.`
      )
    ) {
      return;
    }

    try {
      const authData = JSON.parse(localStorage.getItem("adminAuth"));
      const res = await fetch(`${ApiBaseUrl}/admin/career/applications/${appId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${authData?.token || ""}`,
        },
      });

      const data = await res.json();
      if (data.success) {
        setApplications((prev) =>
          prev.filter((a) => a.applicationId !== appId && a._id !== appId)
        );
        fetchStats();
      } else {
        alert(data.message || "Failed to delete application");
      }
    } catch (err) {
      console.error(err);
      alert("Error deleting application");
    }
  };

  const openStatusModal = (app) => {
    setStatusModal({
      isOpen: true,
      application: app,
      status: app.status || "pending",
      remarks: "",
      sendEmailAlert: true,
      sendSmsAlert: true,
      interviewDate: app.interviewDetails?.scheduledDate
        ? new Date(app.interviewDetails.scheduledDate).toISOString().split("T")[0]
        : "",
      interviewTime: app.interviewDetails?.scheduledTime || "",
      interviewMode: app.interviewDetails?.mode || "Online (Google Meet)",
      interviewLink: app.interviewDetails?.locationOrLink || "",
      saving: false,
    });
  };

  const handleSaveStatus = async () => {
    const { application, status, remarks, sendEmailAlert, sendSmsAlert, interviewDate, interviewTime, interviewMode, interviewLink } = statusModal;
    if (!application) return;

    setStatusModal((prev) => ({ ...prev, saving: true }));

    try {
      const authData = JSON.parse(localStorage.getItem("adminAuth"));
      const payload = {
        status,
        remarks,
        sendEmailAlert,
        sendSmsAlert,
        ...(status === "interview_scheduled" && {
          interviewDetails: {
            scheduledDate: interviewDate,
            scheduledTime: interviewTime,
            mode: interviewMode,
            locationOrLink: interviewLink,
          },
        }),
      };

      const res = await fetch(
        `${ApiBaseUrl}/admin/career/applications/${application.applicationId}/status`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${authData?.token || ""}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();
      if (data.success) {
        setApplications((prev) =>
          prev.map((a) =>
            a.applicationId === application.applicationId
              ? { ...a, status: data.data.status }
              : a
          )
        );
        fetchStats();
        setStatusModal((prev) => ({ ...prev, isOpen: false, saving: false }));
        alert(data.message || "Status updated successfully!");
      } else {
        alert(data.message || "Failed to update status");
        setStatusModal((prev) => ({ ...prev, saving: false }));
      }
    } catch (err) {
      console.error(err);
      alert("Error updating status");
      setStatusModal((prev) => ({ ...prev, saving: false }));
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            Pending Review
          </span>
        );
      case "under_review":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-sky-50 text-sky-700 border border-sky-200">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-500" />
            Under Review
          </span>
        );
      case "shortlisted":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Shortlisted
          </span>
        );
      case "interview_scheduled":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">
            <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
            Interview Scheduled
          </span>
        );
      case "hired":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-300">
            <span className="w-1.5 h-1.5 rounded-full bg-green-600" />
            Hired / Selected
          </span>
        );
      case "rejected":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
            Not Selected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
            {status}
          </span>
        );
    }
  };

  const statusTabs = [
    { key: "All", label: "All Applicants", count: stats.total },
    { key: "pending", label: "Pending", count: stats.pending },
    { key: "under_review", label: "Under Review", count: stats.under_review },
    { key: "shortlisted", label: "Shortlisted", count: stats.shortlisted },
    { key: "interview_scheduled", label: "Interview", count: stats.interview_scheduled },
    { key: "hired", label: "Hired", count: stats.hired },
    { key: "rejected", label: "Rejected", count: stats.rejected },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <Link to="/" className="hover:text-red-600">Dashboard</Link>
            <span>/</span>
            <Link to="/career/jobs" className="hover:text-red-600">Careers</Link>
            <span>/</span>
            <span className="text-gray-900 font-medium">Job Applications</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Users className="w-8 h-8 text-red-600" />
            Candidate Applications
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Review submissions, download candidate CVs, schedule interviews, and notify applicants via Email & SMS.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/career/jobs"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-sm font-semibold shadow-sm transition"
          >
            <Briefcase className="w-4 h-4 text-gray-500" />
            Manage Jobs
          </Link>
          <Link
            to="/career/jobs/add"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold shadow-md transition"
          >
            Post New Job
          </Link>
        </div>
      </div>

      {/* Pipeline Status Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-6 scrollbar-thin">
        {statusTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilters((prev) => ({ ...prev, status: tab.key, page: 1 }))}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition whitespace-nowrap ${
              filters.status === tab.key
                ? "bg-red-600 text-white shadow-sm"
                : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
            }`}
          >
            <span>{tab.label}</span>
            <span
              className={`px-2 py-0.5 rounded-full text-xs ${
                filters.status === tab.key
                  ? "bg-white/20 text-white font-bold"
                  : "bg-gray-100 text-gray-600 font-medium"
              }`}
            >
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm mb-6 space-y-4">
        <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-5 h-5 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by candidate name, email, phone, city, or application ID..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white"
            />
          </div>

          <div className="flex flex-wrap sm:flex-nowrap gap-2">
            <select
              value={filters.jobId}
              onChange={(e) => setFilters({ ...filters, jobId: e.target.value, page: 1 })}
              className="px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-red-500 max-w-[240px]"
            >
              <option value="All">All Job Positions</option>
              <option value="GENERAL">General Applications</option>
              {jobsList.map((job) => (
                <option key={job.jobId} value={job.jobId}>
                  {job.title}
                </option>
              ))}
            </select>

            <button
              type="submit"
              className="px-5 py-2.5 bg-gray-900 hover:bg-gray-800 text-white rounded-xl text-sm font-medium transition"
            >
              Search
            </button>
          </div>
        </form>
      </div>

      {/* Applications Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-16 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600 mx-auto"></div>
            <p className="mt-3 text-sm text-gray-500">Loading applicants data...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">
            <AlertCircle className="w-10 h-10 mx-auto mb-2 opacity-80" />
            <p className="font-semibold">{error}</p>
            <button
              onClick={fetchApplications}
              className="mt-3 px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200"
            >
              Retry
            </button>
          </div>
        ) : applications.length === 0 ? (
          <div className="p-16 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4 text-gray-400">
              <Users className="w-8 h-8" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">No Applications Found</h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto">
              No candidates have applied under this filter category yet.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/75 text-xs font-semibold uppercase text-gray-500 tracking-wider">
                  <th className="px-6 py-4">Applicant</th>
                  <th className="px-6 py-4">Position Applied</th>
                  <th className="px-6 py-4">Experience & City</th>
                  <th className="px-6 py-4">Applied Date</th>
                  <th className="px-6 py-4 text-center">Status</th>
                  <th className="px-6 py-4 text-center">Resume (CV)</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm">
                {applications.map((app) => (
                  <tr key={app._id || app.applicationId} className="hover:bg-gray-50/80 transition">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-semibold text-gray-900 flex items-center gap-2">
                          {app.fullName}
                          {!app.isSeen && (
                            <span className="px-1.5 py-0.5 text-[9px] font-extrabold bg-red-600 text-white rounded">
                              NEW
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3 text-gray-400" />
                            {app.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3 text-gray-400" />
                            {app.phone}
                          </span>
                        </div>
                        <span className="text-[10px] font-mono text-gray-400">
                          ID: {app.applicationId}
                        </span>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{app.jobTitle}</div>
                      {app.jobDepartment && (
                        <div className="text-xs text-gray-500">{app.jobDepartment}</div>
                      )}
                    </td>

                    <td className="px-6 py-4">
                      <div className="text-gray-900 font-medium">{app.experienceYears || "Fresh"}</div>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <MapPin className="w-3 h-3 text-gray-400" />
                        {app.currentCity}
                      </div>
                    </td>

                    <td className="px-6 py-4 text-xs text-gray-500">
                      {new Date(app.createdAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                      <div className="text-[11px] text-gray-400">
                        {new Date(app.createdAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </td>

                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => openStatusModal(app)}
                        className="cursor-pointer hover:opacity-80 transition"
                        title="Click to change status and send Email/SMS alerts"
                      >
                        {getStatusBadge(app.status)}
                      </button>
                    </td>

                    <td className="px-6 py-4 text-center">
                      {app.resume && app.resume.fileName ? (
                        <button
                          onClick={() => handleDownloadCV(app.applicationId || app._id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs font-semibold border border-gray-200 transition"
                          title="Download candidate CV stored on VPS"
                        >
                          <Download className="w-3.5 h-3.5 text-gray-600" />
                          Download CV
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">No CV</span>
                      )}
                    </td>

                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/career/applications/${app.applicationId || app._id}`}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="View Full Application Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>

                        <button
                          onClick={() => openStatusModal(app)}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="Update Status & Send Alerts"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() =>
                            handleDeleteApplication(
                              app.applicationId || app._id,
                              app.fullName
                            )
                          }
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Delete Application & Purge CV from VPS"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              Showing page <strong className="text-gray-800">{filters.page}</strong> of{" "}
              <strong className="text-gray-800">{totalPages}</strong> (Total {totalRecords} applications)
            </span>
            <div className="flex gap-2">
              <button
                disabled={filters.page <= 1}
                onClick={() => setFilters((prev) => ({ ...prev, page: prev.page - 1 }))}
                className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                disabled={filters.page >= totalPages}
                onClick={() => setFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
                className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Quick Status Update Modal */}
      {statusModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-5 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Update Candidate Status
                </h3>
                <p className="text-xs text-gray-500">
                  {statusModal.application?.fullName} — {statusModal.application?.jobTitle}
                </p>
              </div>
              <button
                onClick={() => setStatusModal((prev) => ({ ...prev, isOpen: false }))}
                className="text-gray-400 hover:text-gray-600 font-bold text-lg"
              >
                ✕
              </button>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                New Application Status
              </label>
              <select
                value={statusModal.status}
                onChange={(e) =>
                  setStatusModal((prev) => ({ ...prev, status: e.target.value }))
                }
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="pending">Pending Review</option>
                <option value="under_review">Under Review</option>
                <option value="shortlisted">Shortlisted</option>
                <option value="interview_scheduled">Interview Scheduled</option>
                <option value="hired">Selected / Hired</option>
                <option value="rejected">Not Selected / Rejected</option>
              </select>
            </div>

            {/* Interview scheduling sub-fields if interview_scheduled */}
            {statusModal.status === "interview_scheduled" && (
              <div className="p-4 bg-purple-50/70 border border-purple-200 rounded-xl space-y-3">
                <h4 className="text-xs font-bold text-purple-900 uppercase tracking-wider">
                  Interview Scheduling Details
                </h4>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Date
                    </label>
                    <input
                      type="date"
                      value={statusModal.interviewDate}
                      onChange={(e) =>
                        setStatusModal((prev) => ({
                          ...prev,
                          interviewDate: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Time
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. 03:30 PM (PKT)"
                      value={statusModal.interviewTime}
                      onChange={(e) =>
                        setStatusModal((prev) => ({
                          ...prev,
                          interviewTime: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Meeting Mode & Location / Link
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={statusModal.interviewMode}
                      onChange={(e) =>
                        setStatusModal((prev) => ({
                          ...prev,
                          interviewMode: e.target.value,
                        }))
                      }
                      className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs"
                    >
                      <option value="Online (Google Meet)">Online (Google Meet)</option>
                      <option value="Online (Zoom)">Online (Zoom)</option>
                      <option value="In-Person Office">In-Person (Lahore Office)</option>
                      <option value="Phone Discussion">Phone Discussion</option>
                    </select>

                    <input
                      type="text"
                      placeholder="e.g. meet.google.com/xxx-xxxx-xxx or office address"
                      value={statusModal.interviewLink}
                      onChange={(e) =>
                        setStatusModal((prev) => ({
                          ...prev,
                          interviewLink: e.target.value,
                        }))
                      }
                      className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs"
                    />
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                HR Remarks / Feedback Note
              </label>
              <textarea
                rows="3"
                placeholder="Optional comments or feedback included in the status update..."
                value={statusModal.remarks}
                onChange={(e) =>
                  setStatusModal((prev) => ({ ...prev, remarks: e.target.value }))
                }
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white"
              />
            </div>

            {/* Notification checkboxes */}
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-2">
              <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                Candidate Alert Notifications
              </h4>

              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={statusModal.sendEmailAlert}
                  onChange={(e) =>
                    setStatusModal((prev) => ({
                      ...prev,
                      sendEmailAlert: e.target.checked,
                    }))
                  }
                  className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                />
                <span className="text-xs font-semibold text-gray-800">
                  Send Email Alert to {statusModal.application?.email}
                </span>
              </label>

              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={statusModal.sendSmsAlert}
                  onChange={(e) =>
                    setStatusModal((prev) => ({
                      ...prev,
                      sendSmsAlert: e.target.checked,
                    }))
                  }
                  className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                />
                <span className="text-xs font-semibold text-gray-800">
                  Send SMS Alert to {statusModal.application?.phone}
                </span>
              </label>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setStatusModal((prev) => ({ ...prev, isOpen: false }))}
                className="px-4 py-2 border border-gray-200 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={statusModal.saving}
                onClick={handleSaveStatus}
                className="inline-flex items-center gap-2 px-6 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl shadow-md transition disabled:opacity-50"
              >
                {statusModal.saving ? "Saving & Sending..." : "Update Status & Notify"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareerApplicationsList;
